import {
  toWSDateString,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  InvoiceStatus,
  IPayableSchema,
  IPayrollSettings
} from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { calculateLineItemsTotal } from "../../utils";
import {
  buildPayableIcon,
  PayablesFilters,
  PayablesTableItemData
} from "./PayablesTable";
import { getStatusColumn } from "./getStatusColumn";
import { IPayeeRow } from "../../../services/search";

export const PayableNameCell: React.FC<{
  payable?: IPayableSchema;
  payee?: IPayeeRow;
}> = ({ payable, payee }) => {
  if (!payable || !payee) {
    return <>-</>;
  }

  const name = wsName({
    user: payee.user,
    member: payee.member,
    payerOwnedData: payee.payerOwnedData
  });

  return (
    <WSTableCell
      avatar={{
        type: "icon",
        ...buildPayableIcon(payable)
      }}
      text={name.getResolvedName()}
      secondaryText={name.getResolvedSecondaryName()}
      truncationOptions={{
        text: { allowTwoLines: true }
      }}
    />
  );
};

export const getColumns = (props: {
  showDateRequested?: boolean;
  showDueDate?: boolean;
  showDatePaid?: boolean;
  showDateExpectedPay?: boolean;
  payrollSettings?: IPayrollSettings;
  sendPaymentsPaidAtLabel?: string;
  filters: PayablesFilters;
  currencyCode?: string;
  setFilters: (newFilters: PayablesFilters) => void;
}): WSTableColumn<PayablesTableItemData>[] => [
  {
    config: {
      gridTemplateSizeMax: "2fr",
      header: {
        text: "Name"
      }
    },
    renderFunction: ({ data: { payable, payee } }) => {
      return <PayableNameCell payable={payable} payee={payee} />;
    }
  },

  {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS", "S"],
      header: {
        text: "Invoice #"
      }
    },
    renderFunction: ({ data: { payable } }) => (
      <WSTableCell
        text={payable?.invoiceNumber}
        secondaryText={payable?.lineItems[0].description}
      />
    )
  },
  {
    config: {
      header: {
        text: `Amount ${props.currencyCode ? `(${props.currencyCode})` : ""}`
      }
    },
    renderFunction: ({ data: { payable } }) => {
      if (!payable) {
        return null;
      }

      return (
        <WSTableCell
          text={formatMoney(
            calculateLineItemsTotal(payable.lineItems) +
              (payable.chargedFees?.lateFee?.amount || 0),
            payable.currency
          )}
        />
      );
    }
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: props.showDateRequested
        ? ["XS", "S"]
        : ["XS", "S", "M", "L", "XL"],
      header: {
        text: "Opened date"
      }
    },
    renderFunction: ({ data: { payable } }) =>
      payable?.status === InvoiceStatus.Pending ? (
        <WSTableCell text="Eligibility pending" />
      ) : (
        <WSTableCell
          text={toWSDateString(payable?.events.openedAt, "monthDayYear")}
        />
      )
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: props.showDueDate
        ? ["XS", "S"]
        : ["XS", "S", "M", "L", "XL"],
      header: {
        text: "Due date"
      }
    },
    renderFunction: ({ data: { payable } }) => (
      <WSTableCell
        text={toWSDateString(
          payable?.client.payDate || payable?.dueDate,
          "monthDayYear"
        )}
        secondaryText={payable?.client.payDate ? "Updated" : ""}
      />
    )
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: props.showDatePaid
        ? ["XS", "S"]
        : ["XS", "S", "M", "L", "XL"],
      header: {
        text: props.sendPaymentsPaidAtLabel
      },
      sortDirection:
        props.filters.sort?.["events.paidAt"] === "asc"
          ? "ascending"
          : "descending",
      onColumnSort(direction) {
        return props.setFilters({
          sort: {
            "events.paidAt": direction === "ascending" ? "asc" : "desc"
          }
        });
      }
    },
    renderFunction: ({ data: { payable } }) => (
      <WSTableCell
        text={toWSDateString(payable?.events.paidAt, "monthDayYear")}
      />
    )
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: props.showDateExpectedPay
        ? ["XS", "S"]
        : ["XS", "S", "M", "L", "XL"],
      header: {
        text: "Expected pay date"
      }
    },
    renderFunction: ({ data: { payrollRun } }) => (
      <WSTableCell
        text={
          payrollRun?.date
            ? toWSDateString(payrollRun.date, "monthDayYear")
            : "Future payroll"
        }
      />
    )
  },
  {
    config: {
      header: {
        text: "Status"
      },
      gridTemplateSizeMax: "1.5fr"
    },
    renderFunction: ({ data: { payable } }) => {
      if (!payable || !props.payrollSettings) {
        return null;
      }

      return (
        <WSTableCell {...getStatusColumn(payable, props.payrollSettings)} />
      );
    }
  }
];
